import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Bell, UserPlus, Check, Calendar, MessageCircle, ChevronRight, Trash2 } from 'lucide-react';

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [pendingConnections, setPendingConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [notifResponse, connResponse] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/connections`)
      ]);
      setNotifications(notifResponse.data.notifications || []);
      setPendingConnections(connResponse.data.pending_requests || []);
      // Mark all notifications as read when viewing the page
      try {
        await axios.post(`${API}/notifications/read-all`);
        window.dispatchEvent(new CustomEvent('notifications-updated'));
      } catch {}
      // Clear app icon badge when user views notifications
      if (navigator.clearAppBadge) {
        navigator.clearAppBadge().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      if (navigator.clearAppBadge) {
        navigator.clearAppBadge().catch(() => {});
      }
      // Tell Navbar to refresh counts immediately
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const deleteReadNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications/read`);
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (error) {
      console.error('Failed to delete read notifications:', error);
    }
  };

  const handleAccept = async (userId) => {
    try {
      await axios.post(`${API}/connections/accept/${userId}`);
      setPendingConnections(prev => prev.filter(c => c.user_id !== userId));
    } catch (error) {
      console.error('Failed to accept connection:', error);
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.post(`${API}/connections/reject/${userId}`);
      setPendingConnections(prev => prev.filter(c => c.user_id !== userId));
    } catch (error) {
      console.error('Failed to reject connection:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection_accepted':
        return <UserPlus className="w-5 h-5 text-green-600" />;
      case 'connection_request':
        return <UserPlus className="w-5 h-5 text-primary" />;
      case 'event_rsvp':
        return <Calendar className="w-5 h-5 text-green-600" />;
      case 'attendance_request':
        return <Calendar className="w-5 h-5 text-purple-600" />;
      case 'attendance_approved':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'attendance_declined':
        return <Calendar className="w-5 h-5 text-red-500" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-20 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold font-heading">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-muted-foreground">{unreadCount} unread</p>
                )}
              </div>
              <div className="flex gap-2">
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                    <Check className="w-4 h-4 mr-1" />
                    Mark all read
                  </Button>
                )}
                {notifications.some(n => n.read) && (
                  <Button variant="ghost" size="sm" onClick={deleteReadNotifications} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear read
                  </Button>
                )}
              </div>
            </div>

            {/* Pending Connection Requests */}
            {pendingConnections.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  Connection Requests
                </h2>
                <div className="space-y-3">
                  {pendingConnections.map(conn => (
                    <div
                      key={conn.user_id}
                      className="bg-white rounded-xl p-4 border-2 border-primary/20 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <Link to={`/profile/${conn.user_id}`}>
                          {conn.profile_picture ? (
                            <img
                              src={conn.profile_picture}
                              alt={conn.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                              {conn.name?.charAt(0)}
                            </div>
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/profile/${conn.user_id}`} className="font-semibold hover:text-primary truncate block">
                            {conn.name}
                          </Link>
                          <p className="text-sm text-muted-foreground truncate">
                            {conn.residence_city}, {conn.residence_country}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAccept(conn.user_id)}
                            className="rounded-full"
                          >
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(conn.user_id)}
                            className="rounded-full"
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notifications List */}
            <div>
              {notifications.length > 0 && (
                <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  Recent Activity
                </h2>
              )}
              
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : notifications.length === 0 && pendingConnections.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No notifications yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    When someone accepts your connection or interacts with you, you'll see it here
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.map(notif => {
                    // Determine the link - fallback to /groups for permission notifications
                    let notifLink = notif.link || (notif.type === 'permission' ? '/groups' : '#');
                    // For comment notifications without a hash, scroll to comments section
                    if (notif.type === 'event_comment' && notifLink.startsWith('/events/') && !notifLink.includes('#')) {
                      notifLink = notifLink + '#comments';
                    }
                    // For follow-up notifications, scroll to add comment form
                    if (notif.type === 'event_followup' && notifLink.startsWith('/events/') && !notifLink.includes('#')) {
                      notifLink = notifLink + '#add-comment';
                    }
                    // For reminder notifications, scroll to comments if no hash
                    if (notif.type === 'event_reminder' && notifLink.startsWith('/events/') && !notifLink.includes('#')) {
                      notifLink = notifLink + '#comments';
                    }
                    return (
                    <Link
                      key={notif.notification_id}
                      to={notifLink}
                      onClick={() => !notif.read && markAsRead(notif.notification_id)}
                      className={`block rounded-xl p-4 border-2 transition-colors ${
                        notif.read 
                          ? 'bg-white border-border' 
                          : 'bg-primary/5 border-primary/20'
                      } hover:border-primary/40`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.read ? 'bg-muted' : 'bg-primary/10'
                        }`}>
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${notif.read ? 'text-foreground' : 'text-foreground'}`}>
                            {notif.title}
                          </p>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTime(notif.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteNotification(notif.notification_id);
                            }}
                            className="p-1 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600 transition-colors"
                            title="Delete notification"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </Link>
                  );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
